import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import odata from '@bingads-webui-component-legacy/http/channel/odata';

import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { ImageExperience as ImageExperienceInternal, AdTypeEnum } from '@bingads-webui-campaign-react/image-experience';
import { useCreateChildPerfMarker } from '@bingads-webui-campaign-react/performance-marker-provider';
import { instrumentationScenarios } from '@bingads-webui-campaign/ad-creator-tools-instrumentation';
import { getAdDataFromAssetGroupData } from './ad-assets-previewer-utils';
import { MaxImageCount, extraPlacementInfo } from '../const';

// init a legacy i18n for legacy web ui shared components.
// set it in window variable since it is referenced like this in legacy components.
export const setGlobalLegacyI18n = (i18n) => {
  window.globalLegacyI18n = {
    get: () => i18n,
  };
};

const decomposeUrl = (url = '') => {
  const protocol = url.match(/^(https?:\/\/)/i);
  const destination = url.replace(protocol, '');

  return {
    protocol: protocol ? protocol[0] : '',
    destination,
  };
};

const combineUrl = (protocol, destination) => protocol + destination;

export const ImageExperience = (props) => {
  const {
    i18n,
    preferenceService,
    oDataConfig,
    suggestion,
    suggestionPoller,
    aid,
    cid,
    campaignId,
    assetGroupData,
    previewTextAssets,
    imagesContainerRef,
    imageListState: {
      imageList,
      selectedImageId,
      cropImageObj,
      editingPlacementType,
    },
    setAddNewImagesCallback,
    onImageListUpdated,
    onImageSelected,
    onImageRemoved,
    onSetCropImageObj,
    onSetEditingPlacementType,
    onFinalUrlUpdated,
    bypassSettingGlobalLegacyI18n,
    assetLibraryEventHandler,
  } = props;

  const isCopilotEnabled = true;

  if (!bypassSettingGlobalLegacyI18n) {
    setGlobalLegacyI18n(i18n);
  }

  const finalUrl = _.get(assetGroupData, ['FinalUrls', 0]);
  const adData = useMemo(() => (
    getAdDataFromAssetGroupData(assetGroupData, previewTextAssets)
  ), [assetGroupData, previewTextAssets]);

  const mediaRepository = useMemo(() => new MediaRepository({
    aid,
    cid,
    cmpId: campaignId,
    oDataConfig,
  }), [cid, aid, campaignId, oDataConfig]);

  const perfMarker = useCreateChildPerfMarker('PerfMaxImageExperienceWrapper');

  const addImagesCallbackRef = useRef(_.noop);

  useEffect(() => {
    setAddNewImagesCallback(() => (images, imageSource) =>
      addImagesCallbackRef.current(images, imageSource));
  }, [setAddNewImagesCallback]);

  const onImageEditCancelled = useCallback(() => {
    onSetCropImageObj(null);
    onSetEditingPlacementType('');
  }, [onSetCropImageObj, onSetEditingPlacementType]);

  const { protocol, destination } = useMemo(() => decomposeUrl(finalUrl), [finalUrl]);

  const onUpdateProtocol = useCallback((_ev, prefix) => {
    const newUrl = combineUrl(prefix.text, destination);

    onFinalUrlUpdated(newUrl);
  }, [onFinalUrlUpdated, destination]);

  const onUpdateDestination = useCallback((ev) => {
    const newUrl = combineUrl(protocol, _.get(ev, ['target', 'value']));

    onFinalUrlUpdated(newUrl);
  }, [onFinalUrlUpdated, protocol]);

  return (
    <div className="image-experience-content">
      <ImageExperienceInternal
        i18n={i18n}
        perfMarker={perfMarker}
        permissions={{
          dynamic: {
            AssetLibraryAIGCMVP: isCopilotEnabled,
          },
        }}
        preferencesService={preferenceService}
        odata={odata}
        oDataConfig={oDataConfig}
        mediaRepository={mediaRepository}
        cid={cid}
        aid={aid}
        campaignId={campaignId}
        suggestion={suggestion}
        suggestionPoller={suggestionPoller}
        imagesPortalContainer={imagesContainerRef.current}
        customMaxImageCount={MaxImageCount}
        shortHeadline={adData.shortHeadline}
        longHeadline={adData.longHeadline}
        description={adData.description}
        businessName={adData.businessName}
        url={adData.url}
        setAddNewImagesCallback={(callback) => { addImagesCallbackRef.current = callback; }}

        isForVNext
        isAudienceAd
        isMultimediaAdinMSANEnabled
        isMultimediaAd={false}
        isCombinedMsanAndMmaAspectRatios
        isStockPhotoEnabled
        isVideo={false}
        isAdCreatorEnabled
        isFinalUrlImageRetrievalEnabled
        isRenderAddInImageList
        isLiteLandingPageEnabled
        disableThumbsUpDown
        enableUnifiedAdPreview

        finalUrlProtocol={protocol}
        finalUrl={destination}
        imageList={imageList}
        currentSelectedImageId={selectedImageId}
        cropImage={cropImageObj}
        adCreatorScenario={instrumentationScenarios.default}
        editingPlacementType={editingPlacementType}
        adPreviewConfig={{
          adType: AdTypeEnum.pMax,
          extraPlacementInfo,
        }}

        onUpdateFinalUrlPrefix={onUpdateProtocol}
        onUpdateFinalUrl={onUpdateDestination}
        onImageSelected={onImageSelected}
        onImageRemoved={onImageRemoved}
        onImageListUpdated={onImageListUpdated}
        onImageEditCancelled={onImageEditCancelled}
        assetLibraryEventHandler={assetLibraryEventHandler}
      />
    </div>
  );
};

ImageExperience.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  suggestion: PropTypes.shape({}).isRequired,
  suggestionPoller: PropTypes.shape({}).isRequired,
  aid: PropTypes.string.isRequired,
  cid: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  assetGroupData: PropTypes.shape({}).isRequired,
  previewTextAssets: PropTypes.shape({}).isRequired,
  imagesContainerRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  imageListState: PropTypes.shape({
    imageList: PropTypes.arrayOf(PropTypes.shape({})),
    selectedImageId: PropTypes.string,
    cropImageObj: PropTypes.shape({
      Id: PropTypes.number.isRequired,
      Type: PropTypes.string.isRequired,
    }),
    editingPlacementType: PropTypes.string,
  }).isRequired,
  selectedImageId: PropTypes.string.isRequired,
  onImageListUpdated: PropTypes.func.isRequired,
  onImageSelected: PropTypes.func.isRequired,
  onImageRemoved: PropTypes.func.isRequired,
  onSetCropImageObj: PropTypes.func.isRequired,
  onSetEditingPlacementType: PropTypes.func.isRequired,
  onFinalUrlUpdated: PropTypes.func.isRequired,
  bypassSettingGlobalLegacyI18n: PropTypes.bool.isRequired,
  setAddNewImagesCallback: PropTypes.func.isRequired,
  assetLibraryEventHandler: PropTypes.shape({}),
};

ImageExperience.defaultProps = {
  assetLibraryEventHandler: null,
};

